import { useRef } from 'react';
import { useQuotationWorkspaceStore } from '../../store/quotationWorkspaceStore';

interface Props {
  master: React.ReactNode;
  detail: React.ReactNode;
  /** Optional banner above the detail panel — used today for the pinned chip
   *  strip; keeps the master pane uncluttered. */
  detailHeader?: React.ReactNode;
}

// Two-pane resizable layout. Master (list) left, detail (form/summary)
// right. Reuses the drag pattern from QuotationComposeForm (mousemove +
// release listeners on the window). The master pane is fixed-width pixels
// (clamped to 290-330px in the store) so the queue list reads predictably
// regardless of viewport — drag still works inside that range for users
// who want a tiny customization.

export function QuotationWorkspaceLayout({ master, detail, detailHeader }: Props) {
  const masterPanelWidthPx = useQuotationWorkspaceStore((s) => s.masterPanelWidthPx);
  const setMasterPanelWidthPx = useQuotationWorkspaceStore((s) => s.setMasterPanelWidthPx);
  const splitRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();

    const onMove = (ev: MouseEvent) => {
      // Width = pointer distance from the container's left edge. Store
      // clamps to [290, 330] so we don't have to.
      setMasterPanelWidthPx(ev.clientX - rect.left);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  return (
    <div ref={splitRef} className="flex h-full min-h-0 w-full">
      <div
        className="relative flex min-w-0 flex-col border-r"
        style={{ flex: `0 0 ${masterPanelWidthPx}px` }}
      >
        {master}
      </div>
      <div
        onMouseDown={handleDragStart}
        className="w-1 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-primary/30"
        aria-hidden
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {detailHeader}
        <div className="min-h-0 flex-1">{detail}</div>
      </div>
    </div>
  );
}
